import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";

import { DISCREPANCY_MUTATION_REFETCH_QUERIES } from "./discrepancyRefetchQueries";
import { type ResolveRow } from "./resolveWithValue.helpers";

export const RESOLVE_QUANTITY_WITH_BOX_18A = gql(`
  mutation ResolveQuantityWithBox18a($discrepancyId: ID!, $amountPounds: Float!, $note: String) {
    resolveQuantityWithBox18a(
      discrepancyId: $discrepancyId
      amountPounds: $amountPounds
      note: $note
    ) {
      id
      processedManifestId
      field
      status
      resolvedAt
      resolutionPath
      resolutionNote
    }
  }
`);

/** The typed pounds as a positive number, or null when blank, not a number, or not above zero. */
export function parseBox18aAmount(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  // Number("12abc") is NaN where parseFloat would read 12, matching the server's finite check.
  // ast-grep-ignore: no-unsafe-number-parsing
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/** The Box 18a answer is offered for one row whose e-Manifest line has Box 18a Quantity checked. */
export function isBox18aOffered(row: ResolveRow | undefined): boolean {
  return row?.eManifestBox18a != null;
}

export const BOX_18A_MANUAL_ENTRY_REASON =
  "A manual entry is waiting on a fix. Undo it first to resolve with the Box 18a amount.";

/** Why the offered Box 18a option can't be picked for this row, or null when it can. */
export function box18aDisabledReason(row: ResolveRow): string | null {
  const isPendingManualEntry = row.status === "PENDING" && row.resolutionPath === "MANUAL_ENTRY";
  return isPendingManualEntry ? BOX_18A_MANUAL_ENTRY_REASON : null;
}

/** Calls resolveQuantityWithBox18a for the dialog's single row with the typed amount and note. */
export function useBox18aSave(args: {
  row: ResolveRow | undefined;
  box18aAmount: string;
  note: string;
  clearSelection: () => void;
  onClose: () => void;
  notifyMutated: () => void;
}) {
  const alerts = useAlerts();
  const [resolve, { loading }] = useMutation(RESOLVE_QUANTITY_WITH_BOX_18A, {
    refetchQueries: DISCREPANCY_MUTATION_REFETCH_QUERIES,
  });

  const save = async () => {
    const amountPounds = parseBox18aAmount(args.box18aAmount);
    if (!args.row || amountPounds == null) return;
    try {
      await resolve({
        variables: {
          discrepancyId: args.row.id,
          amountPounds,
          note: args.note || null,
        },
      });
      alerts.success("Resolved with the Box 18a amount.");
      args.notifyMutated();
      args.clearSelection();
      args.onClose();
    } catch (err) {
      alerts.error("Failed to resolve the discrepancy.", err instanceof Error ? err : undefined);
    }
  };

  return { save, saving: loading };
}
